import React from 'react';
import Button from '../common/Button';
import LeadStatusBadge from './LeadStatusBadge';
import HeatLevelIndicator from './HeatLevelIndicator';
import { format } from 'date-fns';

/**
 * LeadTable Component
 *
 * Desktop table view for leads with sortable columns and quick actions.
 * Hidden on mobile (<768px) in favor of LeadCardView.
 *
 * @param {Array} leads - Array of lead objects
 * @param {function} onView - View lead callback
 * @param {function} onEdit - Edit lead callback
 * @param {string} sortBy - Current sort field
 * @param {string} sortOrder - Current sort order (ASC/DESC)
 * @param {function} onSort - Sort callback (field, order) => void
 * @param {function} onHeatLevelChange - Heat level change callback (assignmentId, level) => void
 * @param {boolean} hideActions - Hide edit actions (for agents)
 */
function LeadTable({
  leads,
  onView,
  onEdit,
  sortBy = 'createdAt',
  sortOrder = 'DESC',
  onSort,
  onHeatLevelChange,
  hideActions = false
}) {
  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => {
    const isActive = sortBy === field;
    const isSortable = onSort && field;

    const handleClick = () => {
      if (isSortable) {
        const newOrder = isActive && sortOrder === 'ASC' ? 'DESC' : 'ASC';
        onSort(field, newOrder);
      }
    };

    return (
      <th
        onClick={handleClick}
        className={`
          px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider
          ${isSortable ? 'cursor-pointer hover:bg-gray-100 transition-colors select-none' : ''}
          ${className}
        `}
      >
        <div className="flex items-center gap-2">
          <span>{children}</span>
          {isSortable && isActive && (
            <svg
              className={`w-4 h-4 transition-transform ${sortOrder === 'ASC' ? 'rotate-180' : ''}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          )}
          {isSortable && !isActive && (
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z"/>
            </svg>
          )}
        </div>
      </th>
    );
  };

  if (leads.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
        <p className="mt-1 text-sm text-gray-500">Try adjusting your filters or search criteria.</p>
      </div>
    );
  }

  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-tenq-gray-light">
          <tr>
            <SortableHeader field="name">Lead</SortableHeader>
            <SortableHeader field="heatLevel">Heat</SortableHeader>
            <SortableHeader field="status">Status</SortableHeader>
            <SortableHeader field="agentName">Assigned To</SortableHeader>
            <SortableHeader field="assignedOn">Assigned On</SortableHeader>
            <SortableHeader field="lastActivity">Last Activity</SortableHeader>
            <SortableHeader field="appointmentDate">Appointment</SortableHeader>
            <SortableHeader field="">Notes</SortableHeader>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {leads.map((lead) => {
            // Get the latest assignment
            const latestAssignment = lead.assignments && lead.assignments.length > 0
              ? lead.assignments[lead.assignments.length - 1]
              : null;
            const status = latestAssignment?.status || 'new';
            const heatLevel = latestAssignment?.heatLevel;
            const lastActivity = latestAssignment?.lastActivityAt || lead.createdAt;

            return (
              <tr
                key={lead.id}
                onClick={() => onView(lead)}
                className="hover:bg-tenq-gray-light transition-colors cursor-pointer"
              >
                {/* Lead Column */}
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{lead.name || 'Unknown'}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {lead.phoneNumber && (
                        <>
                          <a
                            href={`tel:+${lead.phoneNumber.replace(/[^0-9]/g, '')}`}
                            className="text-tenq-gold hover:text-tenq-gold/80"
                            title="Call"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                            </svg>
                          </a>
                          <a
                            href={`https://wa.me/${lead.phoneNumber.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-700"
                            title="WhatsApp"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                          </a>
                        </>
                      )}
                      <span className="text-sm text-gray-500">{lead.phoneNumber || 'No phone'}</span>
                    </div>
                  </div>
                </td>

                {/* Heat Level Column */}
                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <HeatLevelIndicator
                    level={heatLevel}
                    onChange={(level) => {
                      if (onHeatLevelChange && latestAssignment) {
                        onHeatLevelChange(latestAssignment.id, level);
                      }
                    }}
                    size="sm"
                  />
                </td>

                {/* Status Column */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <LeadStatusBadge status={status} size="sm" />
                </td>

                {/* Assigned To Column */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {latestAssignment?.agent?.fullName || '-'}
                  {(latestAssignment?.agent?.leader?.fullName || latestAssignment?.assignedLeader?.fullName) && (
                    <div className="text-xs text-gray-500">
                      ({latestAssignment?.agent?.leader?.fullName || latestAssignment?.assignedLeader?.fullName})
                    </div>
                  )}
                </td>

                {/* Assigned On Column */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {latestAssignment?.assignedAt
                    ? format(new Date(latestAssignment.assignedAt), 'MMM dd, yyyy')
                    : '-'}
                </td>

                {/* Last Activity Column */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {format(new Date(lastActivity), 'MMM dd, yyyy')}
                </td>

                {/* Appointment Column */}
                <td className="px-6 py-4 text-sm text-gray-900">
                  {latestAssignment?.appointmentDate ? (
                    <>
                      <div>{format(new Date(latestAssignment.appointmentDate), 'MMM dd, yyyy')}</div>
                      <div className="text-xs text-gray-500">{format(new Date(latestAssignment.appointmentDate), 'HH:mm')}</div>
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>

                {/* Notes Column */}
                <td className="px-6 py-4 text-sm text-gray-600">
                  {latestAssignment?.appointmentNotes ? (
                    <div className="max-w-xs truncate" title={latestAssignment.appointmentNotes}>
                      {latestAssignment.appointmentNotes}
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>

                {/* Actions Column */}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    {/* View Details Icon */}
                    <button
                      onClick={() => onView(lead)}
                      className="p-2 text-tenq-gold hover:bg-tenq-gold hover:bg-opacity-10 rounded-full transition-colors"
                      title="View Details"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>

                    {/* Edit Icon */}
                    {onEdit && !hideActions && (
                      <button
                        onClick={() => onEdit(lead)}
                        className="p-2 text-tenq-gold hover:bg-tenq-gold hover:bg-opacity-10 rounded-full transition-colors"
                        title="Edit Lead"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default LeadTable;
